// Data-driven company setup progress. Instead of checkboxes the admin ticks,
// each step is detected from REAL data (departments created, employees added,
// payroll entity configured, AI key set, branding uploaded), so the checklist
// always reflects reality and never needs manual dismissal. Every check is
// best-effort: a failing/slow service just leaves that step "to do" — the
// checklist is guidance, never a blocker.
import { useCallback, useEffect, useState } from 'react';
import {
  getDepartments, getAllEmployees, getCurrentTenant, getJobTitles, getLeavePolicies,
  listUsers,
} from '@/lib/hrApi';
import { getAiConfig } from '@/lib/aiConfigApi';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import { holidaysReviewed } from './holidaysReview';

// Roles that count as "delegated" — i.e. you've handed responsibility to someone
// besides yourself (HR, a manager, or a finance approver).
const DELEGATED_ROLES = ['manager', 'hr_manager', 'hr_staff', 'finance', 'hr', 'hr_admin'];

// A brand-new tenant is auto-provisioned with this default department set
// (employee-service inbox_processor on tenant.created). Their mere presence must
// NOT mark "Create departments" done — only a department the admin adds/renames
// beyond these counts.
const DEFAULT_DEPARTMENTS = new Set([
  'executive', 'human resources', 'finance', 'information technology',
  'operations', 'sales', 'marketing',
]);

export interface SetupStep {
  key: string;
  title: string;
  description: string;
  /** Path suffix under the tenant app base (e.g. "admin/departments"). */
  pathSuffix: string;
  cta: string;
  required: boolean;
  done: boolean;
}

interface StepDef extends Omit<SetupStep, 'done'> {
  check: () => Promise<boolean>;
}

const STEP_DEFS: StepDef[] = [
  {
    key: 'departments',
    title: 'Create departments',
    description: 'Set up your company structure so people and approvals roll up correctly.',
    pathSuffix: 'admin/departments',
    cta: 'Add departments',
    required: true,
    check: async () => {
      const d = await getDepartments();
      const items: any[] = d?.items ?? [];
      // Done only once a department exists that isn't one of the auto-seeded
      // defaults — otherwise a fresh tenant shows "done" before the admin acts.
      return items.some((x) => !DEFAULT_DEPARTMENTS.has(String(x?.name ?? '').trim().toLowerCase()));
    },
  },
  {
    key: 'team',
    title: 'Add your team',
    description: 'Import your existing employees from a CSV, or invite them one by one.',
    pathSuffix: 'admin/import',
    cta: 'Add employees',
    required: true,
    // The founder is employee #1, so "added team" means more than just them.
    check: async () => (await getAllEmployees()).length > 1,
  },
  {
    key: 'job_titles',
    title: 'Add job titles',
    description: 'Define the job titles people hold so they stay consistent across your company.',
    pathSuffix: 'admin/job-titles',
    cta: 'Add titles',
    required: false,
    check: async () => {
      const d = await getJobTitles();
      return (d?.total ?? d?.items?.length ?? 0) > 0;
    },
  },
  {
    key: 'roles',
    title: 'Assign roles & approvers',
    description: 'Give your HR, managers, and a finance approver the right access in Users & Roles.',
    pathSuffix: 'admin/users',
    cta: 'Set roles',
    required: false,
    // "Done" once someone other than the founder holds a delegated role.
    check: async () => {
      const res = await listUsers({ page: 1, limit: 100 });
      const users = (res as any)?.users || (res as any)?.items || [];
      return users.some((u: any) => (u.roles || []).some((r: string) => DELEGATED_ROLES.includes(r)));
    },
  },
  {
    key: 'leave_policies',
    title: 'Set leave policies',
    description: 'Configure how much annual, sick, and other leave your team gets.',
    pathSuffix: 'admin/leave-policies',
    cta: 'Set policies',
    required: false,
    check: async () => (await getLeavePolicies())?.length > 0,
  },
  {
    key: 'holidays',
    title: 'Review public holidays',
    description: 'Check the public holidays for your country and adjust which ones your company observes.',
    pathSuffix: 'admin/holidays',
    cta: 'Review holidays',
    required: false,
    // A fresh tenant already has the country's holiday pack, so existence can't mean
    // "reviewed". Done only once the admin has actually opened the Holidays page.
    check: async () => holidaysReviewed(),
  },
  {
    key: 'payroll',
    title: 'Set up payroll',
    description: 'Add a legal entity so you can run payroll and set salaries for your company.',
    pathSuffix: 'payroll',
    cta: 'Set up payroll',
    required: false,
    check: async () => (await payrollApi.listLegalEntities()).length > 0,
  },
  {
    key: 'ai',
    title: 'Connect the AI assistant',
    description: 'Add your AI provider key so your team can ask the assistant about HR and leave.',
    pathSuffix: 'admin/ai-assistant',
    cta: 'Connect AI',
    required: false,
    check: async () => (await getAiConfig()).configured,
  },
  {
    key: 'branding',
    title: 'Add your branding',
    description: 'Upload your logo and set working hours so the app feels like yours.',
    pathSuffix: 'settings',
    cta: 'Open settings',
    required: false,
    check: async () => {
      const t: any = await getCurrentTenant().catch(() => null);
      return !!(t?.logoUrl || t?.logo_url);
    },
  },
];

export interface SetupProgress {
  steps: SetupStep[];
  loading: boolean;
  doneCount: number;
  total: number;
  requiredTotal: number;
  requiredDone: number;
  allRequiredDone: boolean;
  allDone: boolean;
  refresh: () => void;
}

export function useSetupProgress(): SetupProgress {
  const [steps, setSteps] = useState<SetupStep[]>(
    STEP_DEFS.map((d) => ({ ...stripCheck(d), done: false })),
  );
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled(STEP_DEFS.map((d) => d.check()));
    setSteps(
      STEP_DEFS.map((d, i) => ({
        ...stripCheck(d),
        done: results[i].status === 'fulfilled' && results[i].value === true,
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  const doneCount = steps.filter((s) => s.done).length;
  const requiredSteps = steps.filter((s) => s.required);
  const requiredDone = requiredSteps.filter((s) => s.done).length;

  return {
    steps,
    loading,
    doneCount,
    total: steps.length,
    requiredTotal: requiredSteps.length,
    requiredDone,
    allRequiredDone: requiredDone === requiredSteps.length,
    allDone: doneCount === steps.length,
    refresh: run,
  };
}

function stripCheck(d: StepDef): Omit<SetupStep, 'done'> {
  const { check, ...rest } = d;
  void check;
  return rest;
}
